import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useTheme,
  Avatar,
  Badge,
  Icon,
  IconButton,
  alpha,
  Container,
  Divider,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { Edit, Person, Email, Phone, Logout, Settings, Lock } from "@mui/icons-material";
import { clearUserState, updateProfile, logout } from "../../state/slices/user";
import { Switch } from "@mui/material";
import UpdatePasswordForm from "./UpdatePassword";
import { clearTutor, getTutorByUser, setTutor } from "../../state/slices/tutor";
import { updateContentType } from "../../config/config";
import { extractId } from "../../utils/imageIdExtractor";
import { getBusinessAPI, updateBusinessAPI } from "../../services/business";
import { getStudentByUserIdAPI } from "../../services/student";

function Profile() {
  const theme = useTheme();
  const { t } = useTranslation();
  const userState = useSelector((state) => state.user).user;
  let { loading, successMsg } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [submitStatus, setSubmitStatus] = useState("");
  const [businessData, setBusinessData] = useState(null);
  const [businessLoading, setBusinessLoading] = useState(false);
  const [employeeBusinessName, setEmployeeBusinessName] = useState(null);
  const [formData, setFormData] = useState({
    firstName: userState.firstName,
    lastName: userState.lastName,
    email: userState.email,
    phoneNumber: userState.phoneNumber,
    dateOfBirth: userState.dateOfBirth ? new Date(userState.dateOfBirth).toISOString().split('T')[0] : "",
    gender: userState.gender || "",
    roles: userState.roles,
    profileImage: userState.profileImage ? userState.profileImage : "",
    businessName: "",
  });
  const [errors, setErrors] = useState({});
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(location.state?.openPasswordDialog || false);

  useEffect(() => {
    if (location.state?.openPasswordDialog) {
      setPasswordDialogOpen(true);
      // Clear the state to avoid reopening on refresh
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, navigate, location.pathname]);

  // Fetch business data if user is BUSINESS
  useEffect(() => {
    const fetchBusinessData = async () => {
      if (userState.roles.includes("BUSINESS")) {
        setBusinessLoading(true);
        try {
          const response = await getBusinessAPI();
          if (response.data && response.data.business) {
            setBusinessData(response.data.business);
            setFormData(prev => ({
              ...prev,
              businessName: response.data.business.businessName || ""
            }));
          }
        } catch (error) {
          console.error("Error fetching business data:", error);
        } finally {
          setBusinessLoading(false);
        }
      }
    };
    fetchBusinessData();
  }, [userState.roles]);

  // Fetch employee business name if user is STUDENT (employee)
  useEffect(() => {
    const fetchEmployeeBusiness = async () => {
      // Check if user is STUDENT but not BUSINESS or PARENT (i.e., an employee or regular student)
      if (userState.roles.includes("STUDENT") && 
          !userState.roles.includes("BUSINESS") && 
          !userState.roles.includes("PARENT") &&
          userState.id) {
        try {
          const response = await getStudentByUserIdAPI(userState.id);
          if (response.data && response.data.student && response.data.student.businessId) {
            const businessName = response.data.student.businessId.businessName;
            if (businessName) {
              setEmployeeBusinessName(businessName);
            }
          }
        } catch (error) {
          console.error("Error fetching employee business data:", error);
        }
      }
    };
    fetchEmployeeBusiness();
  }, [userState.roles, userState.id]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    validateField(e.target.name, e.target.value);
  };

  const validateField = (fieldName, value) => {
    const validationErrors = { ...errors };

    switch (fieldName) {
      case "firstName":
        validationErrors.firstName = value.trim()
          ? String(value).length < 3
            ? t('validation.firstNameTooShort')
            : ""
          : t('validation.firstNameRequired');
        break;
      case "lastName":
        validationErrors.lastName = value.trim()
          ? String(value).length < 3
            ? t('validation.lastNameTooShort')
            : ""
          : t('validation.lastNameRequired');
        break;
      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        validationErrors.email =
          value.trim() && emailRegex.test(value) ? "" : t('validation.invalidEmail');
        break;
      case "phoneNumber":
        const phoneRegex = /^\+251\d{9}$/;
        validationErrors.phoneNumber =
          value.trim() && phoneRegex.test(value) ? "" : t('validation.phoneNumberInvalid');
        break;
      case "dateOfBirth":
        if (value) {
          const dob = new Date(value);
          const today = new Date();
          if (dob > today) {
            validationErrors.dateOfBirth = t('validation.dateOfBirthInvalid') || "Date of birth cannot be in the future";
          } else {
            validationErrors.dateOfBirth = "";
          }
        } else {
          validationErrors.dateOfBirth = "";
        }
        break;
      case "gender":
        if (value && !["Male", "Female"].includes(value)) {
          validationErrors.gender = t('validation.genderInvalid') || "Please select a valid gender";
        } else {
          validationErrors.gender = "";
        }
        break;
      case "businessName":
        validationErrors.businessName = value.trim()
          ? String(value).length < 2
            ? t('validation.businessNameTooShort')
            : ""
          : "";
        break;
      case "password":
      default:
        break;
    }

    setErrors((prev) => {
      return { ...prev, ...validationErrors };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = {};

    Object.keys(formData).forEach((fieldName) => {
      validateField(fieldName, formData[fieldName]);
      if (errors[fieldName]) {
        validationErrors[fieldName] = errors[fieldName];
      }
    });

    setSubmitStatus((prev) => prev + "x");
  };

  const tutorProfile = () => {
    if (userState.roles.includes("TUTOR")) {
      dispatch(getTutorByUser(userState.id));
      navigate("/tutor-profile");
      dispatch(clearTutor());
    }
  };

  const logoutUser = () => {
    dispatch(clearUserState());
    dispatch(setTutor(null));
    dispatch(logout());
    navigate("/login");
  };

  useEffect(() => {
    if (
      submitStatus &&
      Object.keys(errors).filter((key) => errors[key]).length === 0
    ) {
      const updateUserProfile = async () => {
        const data = new FormData();
        data.set("firstName", formData.firstName);
        data.set("lastName", formData.lastName);
        data.set("email", formData.email);
        data.set("phoneNumber", formData.phoneNumber);
        if (formData.dateOfBirth) {
          data.set("dateOfBirth", formData.dateOfBirth);
        }
        if (formData.gender) {
          data.set("gender", formData.gender);
        }

        if (image) {
          data.set("profileImage", image);
        }

        console.log("data", data.values());

        updateContentType("multipart/form-data");
        dispatch(updateProfile(data));
        updateContentType("application/json");

        // Update business name if user is BUSINESS and businessName has changed
        if (userState.roles.includes("BUSINESS") && businessData) {
          const currentBusinessName = businessData.businessName || "";
          const newBusinessName = formData.businessName || "";
          if (currentBusinessName !== newBusinessName) {
            try {
              await updateBusinessAPI({ businessName: newBusinessName });
              // Refresh business data
              const response = await getBusinessAPI();
              if (response.data && response.data.business) {
                setBusinessData(response.data.business);
              }
            } catch (error) {
              console.error("Error updating business name:", error);
            }
          }
        }

        setSnackbarOpen(true);
        console.log("Form submitted:", formData);
      };

      updateUserProfile();
    } else {
      dispatch(clearUserState());
    }
  }, [submitStatus]);

  const [image, setImage] = useState(null);
  // const [selectedImage, setSelectedImage] = useState(userState ? userState.profileImage?.replace('&export=download','') : null);
  const [selectedImage, setSelectedImage] = useState(userState ? userState.profileImage : null);


  const handleImageUpload = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = handleFileInputChange;
    input.click();
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file.size > 2000000) {
      alert(t('profile.fileTooBig'));
      return;
    }

    setImage(file);
    setSelectedImage(URL.createObjectURL(file));
    handleSubmit(event);
  };

  console.log("selectedImage", selectedImage)
  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        py: 6,
      }}
    >
      {!loading && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={snackbarOpen}
          onClose={() => setSnackbarOpen(false)}
          message={
            <Typography fontWeight={600} fontSize={16} color="white">
              {successMsg
                ? t('profile.profileUpdatedSuccess')
                : t('profile.profileNotUpdated')}
            </Typography>
          }
        />
      )}
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Profile Header Card */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                p: 4,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  width: '200px',
                  height: '200px',
                  background: alpha('#ffffff', 0.1),
                  borderRadius: '50%',
                  transform: 'translate(30%, -30%)',
                }
              }}
            >
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} sm="auto">
                  <Box display="flex" justifyContent="center">
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                      badgeContent={
                        <IconButton
                          size="small"
                          sx={{
                            background: theme.palette.secondary.main,
                            color: theme.palette.primary.main,
                            border: '3px solid white',
                            '&:hover': {
                              background: theme.palette.secondary.dark,
                            }
                          }}
                          aria-label="upload picture"
                          component="span"
                          onClick={handleImageUpload}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      }
                    >
                      {loading ? (
                        <CircularProgress size={120} sx={{ color: 'white' }} />
                      ) : (
                        <Avatar
                          alt={`${formData.firstName} ${formData.lastName}`}
                          sx={{
                            width: 120,
                            height: 120,
                            border: '4px solid white',
                            boxShadow: `0 4px 20px ${alpha('#000000', 0.2)}`,
                          }}
                        >
                          {selectedImage ? (
                            <img
                              crossOrigin="anonymous"
                              src={selectedImage.includes('drive.google.com') ? `https://drive.lienuc.com/uc?id=${extractId(selectedImage)}` : selectedImage}
                              alt="profile"
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <Person sx={{ fontSize: 60 }} />
                          )}
                        </Avatar>
                      )}
                    </Badge>
                  </Box>
                </Grid>
                <Grid item xs={12} sm>
                  <Stack spacing={1}>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      Welcome to Astegne, {formData.firstName}. Let's get started!
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      {userState.roles.map((role) => (
                        <Box
                          key={role}
                          sx={{
                            px: 2,
                            py: 0.5,
                            borderRadius: 2,
                            bgcolor: alpha('#ffffff', 0.2),
                            backdropFilter: 'blur(10px)',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {role}
                          </Typography>
                        </Box>
                      ))}
                      {employeeBusinessName && (
                        <Box
                          sx={{
                            px: 2,
                            py: 0.5,
                            borderRadius: 2,
                            bgcolor: alpha('#ffffff', 0.2),
                            backdropFilter: 'blur(10px)',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {t('profile.underBusiness') || 'Under'}: {employeeBusinessName}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm="auto">
                  <Stack spacing={1}>
                    {userState.roles.includes("TUTOR") && (
                      <Button
                        variant="outlined"
                        onClick={tutorProfile}
                        startIcon={<Settings />}
                        sx={{
                          borderColor: 'white',
                          color: 'white',
                          '&:hover': {
                            borderColor: 'white',
                            bgcolor: alpha('#ffffff', 0.1),
                          }
                        }}
                      >
                        {t('profile.tutorDetailProfile')}
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      onClick={logoutUser}
                      startIcon={<Logout />}
                      sx={{
                        borderColor: 'white',
                        color: 'white',
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: alpha('#ffffff', 0.1),
                        }
                      }}
                    >
                      {t('profile.logout')}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* Profile Form Card */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                p: { xs: 3, md: 4 },
                background: 'white',
                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 3 }}>
                {t('profile.personalInformation')}
              </Typography>

              {userState.errorMsg && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {userState.errorMsg}
                </Alert>
              )}

              <Stack spacing={3}>
                <Grid container spacing={3} sx={{ width: '100%', margin: 0 }}>
                  <Grid item xs={12} sm={6} sx={{ width: '100%', boxSizing: 'border-box', padding: '0 !important', paddingRight: '12px !important' }}>
                    <TextField
                      label={t('profile.firstName')}
                      variant="outlined"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <Person sx={{ mr: 1, color: theme.palette.primary.main, opacity: 0.7 }} />
                        ),
                      }}
                      sx={{
                        width: '100%',
                        boxSizing: 'border-box',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ width: '100%', boxSizing: 'border-box', padding: '0 !important', paddingLeft: '12px !important' }}>
                    <TextField
                      label={t('profile.lastName')}
                      variant="outlined"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <Person sx={{ mr: 1, color: theme.palette.primary.main, opacity: 0.7 }} />
                        ),
                      }}
                      sx={{
                        width: '100%',
                        boxSizing: 'border-box',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                <TextField
                  label={t('profile.email')}
                  variant="outlined"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <Email sx={{ mr: 1, color: theme.palette.primary.main, opacity: 0.7 }} />
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />

                <TextField
                  label={t('profile.phoneNumber')}
                  variant="outlined"
                  name="phoneNumber"
                  placeholder="+251XXXXXXXXX"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <Phone sx={{ mr: 1, color: theme.palette.primary.main, opacity: 0.7 }} />
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />

                {/* Business Name Field - Only for BUSINESS users */}
                {userState.roles.includes("BUSINESS") && (
                  <TextField
                    label={t('profile.businessName')}
                    variant="outlined"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    error={!!errors.businessName}
                    helperText={errors.businessName}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <Person sx={{ mr: 1, color: theme.palette.primary.main, opacity: 0.7 }} />
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                )}

                <Grid container spacing={3} sx={{ width: '100%', margin: 0 }}>
                  <Grid item xs={12} sm={6} sx={{ width: '100%', boxSizing: 'border-box', padding: '0 !important', paddingRight: '12px !important' }}>
                    <TextField
                      label={t('profile.dateOfBirth') || 'Date of Birth'}
                      variant="outlined"
                      name="dateOfBirth"
                      type="date"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      error={!!errors.dateOfBirth}
                      helperText={errors.dateOfBirth}
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        width: '100%',
                        boxSizing: 'border-box',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ width: '100%', boxSizing: 'border-box', padding: '0 !important', paddingLeft: '12px !important' }}>
                    <FormControl fullWidth error={!!errors.gender}>
                      <InputLabel shrink>{t('profile.gender') || 'Gender'}</InputLabel>
                      <Select
                        name="gender"
                        value={formData.gender}
                        onChange={handleChange}
                        displayEmpty
                        sx={{
                          width: '100%',
                          boxSizing: 'border-box',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          }
                        }}
                      >
                        <MenuItem value="">
                          <em>{t('profile.selectGender') || 'Select Gender'}</em>
                        </MenuItem>
                        <MenuItem value="Male">{t('profile.male') || 'Male'}</MenuItem>
                        <MenuItem value="Female">{t('profile.female') || 'Female'}</MenuItem>
                      </Select>
                      {errors.gender && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                          {errors.gender}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                  <Button
                    size="large"
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      color: theme.palette.secondary.main,
                      textTransform: "capitalize",
                      minWidth: "200px",
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.5)}`,
                      },
                      '&:disabled': {
                        background: alpha(theme.palette.primary.main, 0.5),
                      }
                    }}
                    disableElevation
                    variant="contained"
                  >
                    {loading ? (
                      <CircularProgress size={25} color="inherit" />
                    ) : (
                      t('profile.updateProfile')
                    )}
                  </Button>
                </Box>
              </Stack>
            </Card>
          </Grid>

          {/* Password Change Card */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                p: { xs: 3, md: 4 },
                background: 'white',
                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: passwordDialogOpen ? 3 : 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Lock sx={{ color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    {t('profile.changePassword')}
                  </Typography>
                </Box>
                <Switch
                  checked={passwordDialogOpen}
                  onChange={() => setPasswordDialogOpen(!passwordDialogOpen)}
                  inputProps={{ "aria-label": "controlled" }}
                  color="primary"
                />
              </Box>
              {passwordDialogOpen && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <UpdatePasswordForm />
                </>
              )}
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default Profile;
